import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useSessionContext } from 'contexts/session';
import { useAdvocacyContext } from 'contexts/advocacy';
import { useAccountContext } from 'contexts/account';
import { HAS_ADVOCACY_SUBMISSION } from 'pages/solar/states/with-utility-accounts/active/active.api';
import { GET_ACCOUNT_NOTIFICATIONS } from 'hooks/use-account-notifications.hook';
import { addTitleToLastName } from 'pages/advocacy/message/email-form/email-form.utils';

interface Props {
  advocacyCampaignId: number;
  emailBody: string;
  representatives?: Representative[];
  emailSubject: string;
}

export const useCreateCampaignSubmission = ({
  advocacyCampaignId,
  emailBody,
  representatives = [],
  emailSubject,
}: Props) => {
  const { userId } = useSessionContext();
  const { selectedAccountId } = useAccountContext();
  const { campaign } = useAdvocacyContext();
  const validRepresentatives = representatives
    .map(rep => ({
      email: rep.email,
      name: addTitleToLastName(rep),
    }))
    .filter(rep => rep.email);

  const variables = {
    input: {
      accountId: selectedAccountId,
      advocacyCampaignId,
      body: emailBody,
      representatives: validRepresentatives,
      subject: emailSubject,
      userId,
    },
  };

  const getSubmissionData = {
    query: HAS_ADVOCACY_SUBMISSION,
    variables: { advocacyCampaignId: campaign.id, userId },
  };

  const getAccountNotifications = {
    query: GET_ACCOUNT_NOTIFICATIONS,
    variables: { id: selectedAccountId },
  };

  const [useCreateCampaignSubmission, { data, error, loading }] = useMutation(
    CREATE_ADVOCACY_CAMPAIGN_SUBMISSION,
    {
      awaitRefetchQueries: true,
      refetchQueries: [getSubmissionData, getAccountNotifications],
      variables,
    }
  );

  return [useCreateCampaignSubmission, { data, error, loading }] as const;
};

export const CREATE_ADVOCACY_CAMPAIGN_SUBMISSION = gql`
  mutation createAdvocacyCampaignSubmission(
    $input: CreateAdvocacyCampaignSubmissionInput!
  ) {
    createAdvocacyCampaignSubmission(input: $input) {
      id
    }
  }
`;

export const createAdvocacyCampaignSubmissionMock = {
  createAdvocacyCampaignSubmission: {
    id: 5,
  },
};

export const useCreateCampaignSubmissionMock = [
  null,
  {
    data: { id: 5 },
    error: undefined,
    loading: false,
  },
];
